import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../api/admin.js';
import { Avatar } from '../components/Avatar.jsx';

export default function AdminEmployeesPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

    const filters = {
        page: parseInt(searchParams.get('page') || '1', 10),
        role: searchParams.get('role') || '',
        active: searchParams.get('active') || 'true',
        search: searchParams.get('search') || '',
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: ['admin', 'employees', filters],
        queryFn: () => adminApi.listEmployees(filters),
    });

    const updateFilters = (updates) => {
        const next = new URLSearchParams(searchParams);
        for (const [k, v] of Object.entries(updates)) {
            if (v === '' || v === null || v === undefined) next.delete(k);
            else next.set(k, v);
        }
        if (!('page' in updates)) next.delete('page');
        setSearchParams(next);
    };

    const onSearchSubmit = (e) => {
        e.preventDefault();
        updateFilters({ search: searchInput.trim() || null });
    };

    return (
        <>
            <header className="flex items-center justify-between" style={{ marginBottom: 16 }}>
                <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>Employees</h1>
                <Link to="/admin/employees/new" className="btn btn-primary">
                    + Add employee
                </Link>
            </header>

            {/* Filter bar */}
            <div
                className="surface-card flex flex-wrap items-center"
                style={{ padding: 12, marginBottom: 14, gap: 8 }}
            >
                <form onSubmit={onSearchSubmit} className="flex items-center" style={{ gap: 4 }}>
                    <input
                        type="search"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search name or email…"
                        className="input"
                        style={{ width: 240 }}
                    />
                    <button type="submit" className="btn btn-soft">Search</button>
                </form>

                <select
                    value={filters.role}
                    onChange={(e) => updateFilters({ role: e.target.value || null })}
                    className="select"
                    style={{ width: 'auto', minWidth: 130 }}
                >
                    <option value="">All roles</option>
                    <option value="employee">Employees</option>
                    <option value="manager">Managers</option>
                    <option value="admin">Admins</option>
                </select>

                <select
                    value={filters.active}
                    onChange={(e) => updateFilters({ active: e.target.value })}
                    className="select"
                    style={{ width: 'auto', minWidth: 150 }}
                >
                    <option value="true">Active only</option>
                    <option value="false">Deactivated only</option>
                    <option value="all">All</option>
                </select>
            </div>

            {isLoading && <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading…</p>}
            {isError && <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>Could not load employees.</p>}

            {data && (
                <>
                    <div className="surface-card" style={{ overflow: 'hidden' }}>
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                            <thead style={{ background: 'var(--color-bg-secondary)' }}>
                                <tr>
                                    <Th>Name</Th>
                                    <Th>Email</Th>
                                    <Th>Role</Th>
                                    <Th>Department</Th>
                                    <Th>Status</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.data.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            style={{
                                                padding: '40px 16px',
                                                textAlign: 'center',
                                                color: 'var(--color-text-tertiary)',
                                            }}
                                        >
                                            No employees match your filters.
                                        </td>
                                    </tr>
                                ) : (
                                    data.data.map((emp, idx) => (
                                        <tr
                                            key={emp.id}
                                            style={{
                                                borderTop: idx === 0 ? 'none' : '0.5px solid var(--color-border-tertiary)',
                                            }}
                                        >
                                            <Td>
                                                <Link
                                                    to={`/admin/employees/${emp.id}`}
                                                    className="flex items-center"
                                                    style={{
                                                        gap: 10,
                                                        color: 'var(--color-text-primary)',
                                                        textDecoration: 'none',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    <Avatar user={emp} size="sm" />
                                                    <span>{emp.name}</span>
                                                </Link>
                                            </Td>
                                            <Td muted>{emp.email}</Td>
                                            <Td>
                                                <RoleBadge role={emp.role} />
                                            </Td>
                                            <Td muted>{emp.department}</Td>
                                            <Td>
                                                <StatusBadge employee={emp} />
                                            </Td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {data.pagination.total_pages > 1 && (
                        <div
                            className="flex items-center justify-between"
                            style={{ marginTop: 14 }}
                        >
                            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                                Page {data.pagination.page} of {data.pagination.total_pages}
                                {' · '}
                                {data.pagination.total} total
                            </p>
                            <div className="flex" style={{ gap: 8 }}>
                                <button
                                    onClick={() => updateFilters({ page: filters.page - 1 })}
                                    disabled={filters.page <= 1}
                                    className="btn btn-soft"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => updateFilters({ page: filters.page + 1 })}
                                    disabled={filters.page >= data.pagination.total_pages}
                                    className="btn btn-soft"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}

function Th({ children }) {
    return (
        <th
            style={{
                textAlign: 'left',
                fontSize: 11,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--color-text-tertiary)',
                padding: '10px 14px',
            }}
        >
            {children}
        </th>
    );
}

function Td({ children, muted }) {
    return (
        <td
            style={{
                padding: '10px 14px',
                color: muted ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
            }}
        >
            {children}
        </td>
    );
}

function RoleBadge({ role }) {
    const styles = {
        admin: { bg: '#EFE9FA', fg: '#4D32A8' },
        manager: { bg: '#E0EBFA', fg: '#0C447C' },
        employee: { bg: 'var(--color-bg-secondary)', fg: 'var(--color-text-secondary)' },
    };
    const s = styles[role] || styles.employee;
    return (
        <span
            style={{
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 999,
                background: s.bg,
                color: s.fg,
                textTransform: 'capitalize',
            }}
        >
            {role}
        </span>
    );
}

function StatusBadge({ employee }) {
    if (!employee.is_active) {
        return (
            <span className="pill pill-cancelled">Inactive</span>
        );
    }
    if (employee.locked_until && new Date(employee.locked_until) > new Date()) {
        return <span className="pill pill-rejected">🔒 Locked</span>;
    }
    if (employee.must_change_password) {
        return <span className="pill pill-pending">Pwd reset pending</span>;
    }
    return <span className="pill pill-approved">Active</span>;
}
